import * as coursesApi from "@/api/coursesApi.js"
import * as groupApi from "@/api/groupApi.js"
import * as subscriptionsApi from "@/api/subscriptionsApi"

import { loadStripe } from "@stripe/stripe-js/pure"

import * as sentryReporter from "@/services/sentryReporter.js"

import i18n from "@/plugins/i18n"

import { useUserSessionStore } from "@/store/userSession.js"
import { useGroupsStore } from "@/store/groups.js"
import { useConfigurationStore } from "@/store/configuration.js"
import { useCoursesStore } from "@/store/courses.js"

export const courseTypes = {
  IN_PROGRESS: "inprogress",
  COMPLETED: "completed"
}

export const courseStates = {
  COMPLETED: "completed"
}

export class Course {
  constructor({
    id,
    name,
    description,
    image,
    lessons_count,
    user_enrollment,
    lesson_order_enforced,
    dedicated_group,
    enrolled_users_visible,
    completed_users_visible,
    tier,
    followup_button
  }) {
    this.id = id
    this.name = name
    this.description = description
    this.imageUrl = image?.versions?.width_720?.base_url
    this.lessonsCount = lessons_count
    this.enrolled = Boolean(user_enrollment)
    this.enrollingOrUnenrolling = false
    this.restarting = false
    this.justCompleted = false
    this.lessonOrderEnforced = lesson_order_enforced
    this.group = dedicated_group
    this.groupMembers = []
    this.enrolledUsersVisible = enrolled_users_visible
    this.completedUsersVisible = completed_users_visible
    this.followupButton = followup_button
    this.updateCompletedState({ userEnrollment: user_enrollment })
    this.updatePurchasedState({ tier })
  }

  updateCompletedState({ userEnrollment, afterLessonCompletion, fromLesson }) {
    const previousCompleted = this.completed
    this.completed = userEnrollment?.state === courseStates.COMPLETED

    if (afterLessonCompletion) {
      this.justCompleted = this.completed
    }

    if (fromLesson && previousCompleted !== this.completed) {
      this.refreshDescriptionAndButton()
    }
  }

  updatePurchasedState({ tier }) {
    const userSessionStore = useUserSessionStore()
    const ownedProducts = userSessionStore.ownedProducts

    this.tier = tier
    this.free = !tier
    this.purchased = !this.free && ownedProducts.some((product) => product.product_name === tier?.product_name)
    this.needsPurchase = !this.free && !this.purchased
  }

  async enroll() {
    try {
      this.enrollingOrUnenrolling = true

      const groupStore = useGroupsStore()
      const coursesStore = useCoursesStore()
      await coursesApi.enroll({ courseId: this.id })
      await Promise.all([this.refreshDescriptionAndButton(), groupStore.refreshAllGroups(), coursesStore.resetLessons({ courseId: this.id })])
      this.enrolled = true
      this.loadGroupMembers()
    } catch (err) {
      sentryReporter.reportError(err, i18n.global.t("common.somethingWrong"))
    } finally {
      this.enrollingOrUnenrolling = false
    }
  }

  async unenroll() {
    try {
      this.enrollingOrUnenrolling = true

      const groupStore = useGroupsStore()
      const coursesStore = useCoursesStore()
      await coursesApi.unenroll({ courseId: this.id })
      await Promise.all([groupStore.refreshAllGroups(), coursesStore.resetLessons({ courseId: this.id })])
      this.enrolled = false
      this.setUncomplete()
    } catch (err) {
      sentryReporter.reportError(err, i18n.global.t("common.somethingWrong"))
    } finally {
      this.enrollingOrUnenrolling = false
    }
  }

  async restart() {
    try {
      this.restarting = true
      await coursesApi.restart({ courseId: this.id })
      this.setUncomplete()
    } catch (err) {
      sentryReporter.reportError(err, i18n.global.t("common.somethingWrong"))
    } finally {
      this.restarting = false
    }
  }

  setUncomplete() {
    const coursesStore = useCoursesStore()
    coursesStore.restartCourseLessons()
    this.completed = false
    this.refreshDescriptionAndButton()
  }

  async refreshDescriptionAndButton() {
    try {
      const { course } = await coursesApi.getCourse({ courseId: this.id })
      this.description = course.description
      this.followupButton = course.followupButton
    } catch (err) {
      sentryReporter.reportError(err)
    }
  }

  async updateCurrentLesson(lessonId) {
    await coursesApi.updateCurrentLesson({ courseId: this.id, lessonId })
  }

  async loadGroupMembers() {
    if (this.enrolled && this.group.enabled) {
      const { group_members_collage: groupsMembers } = await groupApi.collageMembers({ groupKeys: [this.group.key] })
      this.groupMembers = groupsMembers[this.group.key]
    }
  }

  async purchase() {
    try {
      const stripe = await this.loadStripe()
      const {
        stripe_checkout_session: { id: sessionId }
      } = await subscriptionsApi.checkoutSessionForProduct({ product_id: this.tier.web_pay_product.id })

      stripe.redirectToCheckout({ sessionId })
    } catch (err) {
      if (this.tier?.web_pay_product?.id) {
        sentryReporter.reportError(err, i18n.global.t("course.courseNotCurrentlyAvailable", { productId: this.tier.web_pay_product.id }))
      } else {
        sentryReporter.reportError(err, i18n.global.t("common.somethingWrong"))
      }
    }
  }

  async loadStripe() {
    const options = {}
    const configurationStore = useConfigurationStore()
    if (configurationStore.configuration.stripeConnectAccountId) {
      options.stripeAccount = configurationStore.configuration.stripeConnectAccountId
    }

    return await loadStripe(configurationStore.configuration.stripeKey, options)
  }
}
